/**
 * The two controls the Ankiety tab's two heads draw, and the sentence that says a write did not land.
 *
 * ── Why they are a feature's and not a page's ────────────────────────────────────────────────
 * Promoted with the report on 17 September 2026: the report DIALOG draws the mark and the failure
 * sentence in its own head, and a feature may not reach into a page. What keeps them one component
 * each rather than three buttons is that neither is a button with a glyph in it — the mark carries `aria-busy` and
 * swaps its tick for a hollow ring while the write is in flight, the refusable one keeps
 * `aria-disabled` rather than the attribute so the reason attached to it stays reachable, and both
 * guard the press at the component rather than at the call site. Those are the halves the next copy
 * would lose, and the previous copies had already parted — one head computed its name twice, once
 * for `aria-label` and once for `title`.
 *
 * The GEOMETRY stays at the call site, as `shared/ui/iconButton.module.css` requires: the stream's
 * head button stands on a chip ground and the pane's on a transparent one, so each passes its own
 * class and nothing is declared on both sides.
 */

import { useId, type ReactNode, type Ref } from 'react';

import ember from '~/shared/ui/emberButton.module.css';
import icon from '~/shared/ui/iconButton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { ReviewedIcon, WaitingIcon } from './SurveyIcons';
import styles from './SurveyActions.module.css';

export interface RefusableIconButtonProps {
  /** The control's name when it can be pressed. */
  readonly label: string;
  /**
   * Why it cannot be pressed, which REPLACES the name rather than being appended to it.
   *
   * `null` when it can. A refused control keeps `aria-disabled` and stays in the tab order — a
   * reason nothing can reach is not a reason — and the press is guarded here rather than at each
   * call site.
   */
  readonly refusedReason: string | null;
  readonly onPress: () => void;
  /** The surface's own geometry class. See the header. */
  readonly className: string;
  readonly children: ReactNode;
  /** For a surface that hands focus back to this control when the one that had it disappears. */
  readonly ref?: Ref<HTMLButtonElement>;
}

export function RefusableIconButton({
  label,
  refusedReason,
  onPress,
  className,
  children,
  ref,
}: RefusableIconButtonProps) {
  const name = refusedReason ?? label;
  return (
    <button
      ref={ref}
      type="button"
      className={`${String(icon.iconButton)} ${className}`}
      aria-disabled={refusedReason !== null}
      aria-label={name}
      title={name}
      onClick={() => {
        if (refusedReason === null) onPress();
      }}
    >
      {children}
    </button>
  );
}

export interface MarkReviewedButtonProps {
  /** „Przejrzane" — the word the pill shows, which is also its accessible name. */
  readonly label: string;
  /** „Oznacz tydz. 8 jako przejrzaną — tego nie można cofnąć": the occurrence, and that it is final. */
  readonly description: string;
  /** The write is in flight. Said through `aria-busy` and the glyph, never through a dim. */
  readonly marking: boolean;
  readonly onMark: () => void;
  /** The surface's own GEOMETRY class. The ghost treatment is this component's. */
  readonly className: string;
}

/**
 * „Przejrzane" — mark one occurrence reviewed.
 *
 * Write-once: `POST /survey/assignment/{id}/mark-reviewed` has no inverse, no „odznacz" exists on
 * the contract and no surface draws one. The caller removes this control once `reviewedAt` is set
 * rather than disabling it.
 *
 * ── A labelled ember pill since 5 November 2026 ──────────────────────────────────────────────
 * Owner: the white square with a tick said nothing until hovered, so it became the check and the
 * word (4 November); a day later the owner asked for the app's ember pill around the same content,
 * the treatment the inbox's „Przejrzane, dalej" already wears. The full sentence,
 * which names the occurrence and says the act cannot be undone, is the control's description, so
 * the accessible name is the word the trainer sees.
 */
export function MarkReviewedButton({
  label,
  description,
  marking,
  onMark,
  className,
}: MarkReviewedButtonProps) {
  const descriptionId = useId();
  return (
    <button
      type="button"
      className={`${String(ember.ember)} ${String(ember.pill)} ${String(styles.markPill)} ${className}`}
      aria-disabled={marking}
      aria-busy={marking}
      aria-describedby={descriptionId}
      title={description}
      onClick={() => {
        if (!marking) onMark();
      }}
    >
      {marking ? <WaitingIcon /> : <ReviewedIcon />}
      {label}
      <span id={descriptionId} className={hidden.srOnly}>
        {description}
      </span>
    </button>
  );
}

/**
 * A write that did not land, beside the control that made it.
 *
 * It was at the BOTTOM of the matrix card, under a table the trainer had usually scrolled, and
 * behind the report's own backdrop whenever the mark was made from inside the dialog — so the one
 * thing that needed saying was the one thing nothing said. It stands in whichever head the control
 * stands in, which is why both dialogs and the matrix draw it.
 */
export function MarkFailedAlert({
  failed,
  message,
}: {
  readonly failed: boolean;
  readonly message: string;
}) {
  if (!failed) return null;
  return (
    <p className={styles.alert} role="alert">
      {message}
    </p>
  );
}
